
import { defineComponent, ref, watch, onBeforeUnmount, nextTick, PropType } from 'vue'
import { useStarTheme } from '@/utils/useStarTheme'

interface ColumnTabItem {
  columnCode: string
  columnName: string
  coverImage?: string
  sort?: number
}

export default defineComponent({
  name: 'StarColumnTabs',
  props: {
    list: {
      type: Array as PropType<ColumnTabItem[]>,
      default: () => [],
    },
    modelValue: {
      type: String,
      default: '',
    },
    showImage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const { isLight } = useStarTheme()
    const navRef = ref<HTMLElement | null>(null)
    const isDragging = ref(false)
    const startX = ref(0)
    const scrollLeft = ref(0)
    const hasMoved = ref(false)

    const resetScroll = () => {
      nextTick(() => {
        if (navRef.value) navRef.value.scrollLeft = 0
      })
    }

    watch(
      () => props.list,
      () => resetScroll(),
      { immediate: true }
    )

    const handleMouseMove = (e: MouseEvent) => {
      const nav = navRef.value
      if (!nav) {
        handleMouseUp()
        return
      }

      const rect = nav.getBoundingClientRect()
      const x = e.pageX - rect.left
      const walk = Math.abs(x - startX.value)

      if (walk > 5) {
        hasMoved.value = true
        if (!isDragging.value) isDragging.value = true
        e.preventDefault()
        nav.scrollLeft = scrollLeft.value - (x - startX.value) * 2
      }
    }

    const handleMouseUp = () => {
      isDragging.value = false
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      const nav = navRef.value
      if (!nav) return

      const rect = nav.getBoundingClientRect()
      startX.value = e.pageX - rect.left
      scrollLeft.value = nav.scrollLeft
      isDragging.value = false
      hasMoved.value = false

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    const goCenter = (e: Event) => {
      const target = e.currentTarget as HTMLElement | null
      target?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }

    const clickTab = (item: ColumnTabItem, _index: number, e: MouseEvent) => {
      if (isDragging.value || hasMoved.value) {
        isDragging.value = false
        hasMoved.value = false
        return
      }

      if (item.columnCode === props.modelValue) return

      emit('update:modelValue', item.columnCode)
      emit('change', item)
      goCenter(e)

      isDragging.value = false
      hasMoved.value = false
    }

    onBeforeUnmount(() => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    })

    return {
      navRef,
      isLight,
      isDragging,
      handleMouseDown,
      clickTab,
    }
  },
})
